// @flow

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import { useVersionValue } from '@alphaott/smart-tv-common/src/hooks/useVersionValue'

import { usePlayerContext, useActions, RECOVERY_PHASES } from '../store'
import { HISTORY_LIMIT, withoutEmpty, describePlaybackError } from '../utils/playbackDiagnostics'
import { CANCEL_TIMEOUT } from '../utils/loadGate'

import type { PlaybackErrorDetails } from '../utils/playbackDiagnostics'

// App-side waiting limits. They may cut the engines' own retries short; engine settings are not changed.
// All of them count active time only: no user pause or user seek input, the app visible
// A cycle is up to MAX_ATTEMPTS loads: the first one and the app's own repeats; then the modal
export const MAX_ATTEMPTS = 3
// One attempt without any progress
export const LOAD_TIMEOUT = 15000
// Video that has played and stopped; the first failure of a new cycle while playing
export const STALL_TIMEOUT = 15000
// Between the end of the stop and the next attempt, with the loader on screen
export const RETRY_DELAY = 2000
// The whole cycle: waits for loads, stalls, stops and the pauses between attempts. Playback does not spend it
export const CYCLE_BUDGET = 40000
// With less budget left the next attempt is not started
export const MIN_ATTEMPT_BUDGET = 5000
export const SUCCESS_PROGRESS = 5000
export const LOADER_DELAY = 1000

const TICK = 250
// Timers on TVs may fire late or freeze: one tick never counts for more than this
const MAX_TICK = 1000
// Less than this is not playback progress, seconds
const MIN_PROGRESS = 0.1
// VOD this close to its duration has ended, seconds
const END_GAP = 0.5

// Between attempts: the engine is being stopped, then a short pause
const STOPPING = 'stopping'
const PAUSING = 'pausing'

// Technical text for the report when the cycle knew no cause. The user sees the localized
// general text instead (mwstv_player_stream_error)
export const DEFAULT_ERROR_MESSAGE = 'Stream error occurred. Please try again later.'

// What the player can tell the user about an error: a text of its own (shown as is) or the engine's
// message or code, which the modal puts into a localized sentence. A plain string is a text of its own
export type ErrorText = string | { message?: ?string, code?: string | number }

const toErrorText = (text: ?ErrorText): ?Object =>
  typeof text === 'string' ? { message: text } : text

// The same error as a stable English text for reports, whatever the language of the interface
// eslint-disable-next-line complexity
export const formatTechnicalMessage = (text: ?ErrorText): string => {
  const errorText = toErrorText(text)

  if (errorText?.message) return errorText.message

  if (errorText?.code !== undefined && errorText?.code !== null) {
    return `Stream error occurred. Error code: ${errorText.code}. Please try again later.`
  }

  return DEFAULT_ERROR_MESSAGE
}

const { LOADING, PLAYING, FAILED, DISMISSED } = RECOVERY_PHASES

const isActivePhase = (phase) => phase === LOADING || phase === PLAYING

const roundTime = (value: mixed): ?number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 10) / 10 : undefined

// Older adapters without an intent signal retain their existing seeking behavior.
const isUserSeeking = (player, seeking) => player?.isUserSeeking?.() ?? seeking

const isEnded = (currentTime, duration) =>
  Number.isFinite(duration) && duration > 0 && currentTime >= duration - END_GAP

export type RecoveryAttempt = {
  // 1-based number of the attempt in its cycle
  attempt: number,
  // load_timeout, stall_timeout, cycle_timeout, error or cancel
  cause: string,
  // The engine error that ended the attempt; none for a timeout
  error?: PlaybackErrorDetails,
  // Distinct error codes seen during the attempt: they decide whether a timeout is reported
  codes: Array<string | number>,
  durationMs: number,
  // The engine could not be stopped after this attempt (cancel failed or timed out)
  stopFailed?: boolean,
}

export type RecoveryFailure = {
  // Cause of the last attempt; the report picks its own from the attempts
  cause: string,
  message: string,
  // The error that ended the last attempt and the first error of the cycle
  error?: PlaybackErrorDetails,
  origin?: PlaybackErrorDetails,
  // Distinct error codes of the whole cycle
  cycleErrorCodes: Array<string | number>,
  // Every attempt of the cycle, in order
  attempts: Array<RecoveryAttempt>,
  // Loads actually started in this cycle, 0 to MAX_ATTEMPTS: a failed stop is not a load
  attempt: number,
  // Cycles of this content before this one: 0 for the first, +1 per Retry from the modal
  cycle: number,
  // Last errors of this content, oldest first: diagnostics only
  history: Array<{ at: number, error: PlaybackErrorDetails }>,
  // Playback state taken before the engine was stopped; values that are not available are left out
  snapshot: Object,
}

type UsePlaybackRecoveryOptions = {
  playerRef: { current: any },
  mediaStreams: mixed,
  defaultTimePosition?: number,
  isVisible: boolean,
  onFailure?: (failure: RecoveryFailure) => void,
}

type PlayerErrorOptions = { isTransient?: boolean, details?: PlaybackErrorDetails }

type UsePlaybackRecoveryResult = {
  // message: what the error tells the user (ErrorText); null when it tells nothing (internal ones)
  onPlayerError: (message: ?ErrorText, options?: PlayerErrorOptions) => void,
  onRecordError: (details: PlaybackErrorDetails, message?: ?ErrorText) => void,
  onRetry: () => void,
  onDismiss: () => void,
  // Play pressed by the user: true when it started a new cycle (the modal was closed and the engine
  // is stopped), false when the engine should simply play
  onPlayRequest: () => boolean,
}

// Watches the playback in cycles. A cycle is up to MAX_ATTEMPTS loads of the same content:
// a failed attempt stops the engine, waits for the stop, pauses and loads again, with the loader
// on screen. Only a cycle that runs out of attempts or of budget opens the error modal;
// a retry from the modal starts a new cycle
export const usePlaybackRecovery = ({
  playerRef,
  mediaStreams,
  defaultTimePosition,
  isVisible,
  onFailure,
}: UsePlaybackRecoveryOptions): UsePlaybackRecoveryResult => {
  const [state] = usePlayerContext()
  const { onError, onResetError, onLoading, onStopLoading, onChangeRecovery } = useActions()

  const phaseRef = useRef(state.recovery || LOADING)
  // An attempt switch, a retry, a dismiss, new content or unmount make earlier async work stale
  const generationRef = useRef(0)
  // Current attempt
  const loadElapsedRef = useRef(0)
  const stallElapsedRef = useRef(0)
  const progressRef = useRef(0)
  const hasProgressRef = useRef(false)
  const hadPlaybackRef = useRef(false)
  const lastTimeRef = useRef(null)
  const attemptStartRef = useRef(Date.now())
  const attemptCodesRef = useRef([])
  // Current cycle
  const attemptRef = useRef(1)
  // Loads started in the cycle: the adapter's own load counts as the first
  const loadsRef = useRef(1)
  const budgetRef = useRef(0)
  const attemptsRef = useRef([])
  const cycleRef = useRef(0)
  // STOPPING or PAUSING between attempts, else null
  const switchRef = useRef(null)
  const pauseLeftRef = useRef(0)
  const lastTickRef = useRef(Date.now())
  // Position the next attempt starts from: null for live
  const resumeTimeRef = useRef(defaultTimePosition)
  // Text the modal shows: the last meaningful error message of the cycle, kept across its attempts.
  // null when the cycle knew no cause: the modal then shows the localized general text
  const messageRef = useRef(null)
  // Stop of the engine started by the last failed attempt, while it runs
  const cancelRef = useRef(null)
  // Diagnostics, kept apart from what is shown: the notice goes away when the modal is closed
  const historyRef = useRef([])
  const cycleErrorCodesRef = useRef([])
  const lastProgressAtRef = useRef(null)
  const originRef = useRef(null)
  const snapshotRef = useRef({})
  const onFailureRef = useRef(onFailure)

  onFailureRef.current = onFailure

  const stateRef = useRef(state)
  const isVisibleRef = useRef(isVisible)

  stateRef.current = state
  isVisibleRef.current = isVisible

  const contentVersion = useVersionValue(mediaStreams)

  const setPhase = useCallback(
    (phase) => {
      phaseRef.current = phase
      onChangeRecovery(phase)
    },
    [onChangeRecovery],
  )

  const resetAttempt = useCallback(() => {
    loadElapsedRef.current = 0
    stallElapsedRef.current = 0
    progressRef.current = 0
    hasProgressRef.current = false
    lastTimeRef.current = null
    attemptCodesRef.current = []
    attemptStartRef.current = Date.now()
  }, [])

  // Recovery confirmed, or a new cycle: the budget, the attempts and the causes start again
  const resetCycle = useCallback(() => {
    attemptRef.current = 1
    loadsRef.current = 1
    budgetRef.current = 0
    attemptsRef.current = []
    originRef.current = null
    cycleErrorCodesRef.current = []
    messageRef.current = null
  }, [])

  // isLoaded: the adapter loads by itself (new content); after Retry nothing is loaded yet
  const startCycle = useCallback(
    (isLoaded: boolean) => {
      switchRef.current = null
      resetCycle()
      resetAttempt()

      if (!isLoaded) loadsRef.current = 0

      setPhase(LOADING)
    },
    [resetCycle, resetAttempt, setPhase],
  )

  // eslint-disable-next-line complexity
  const takeSnapshot = useCallback(() => {
    const player = playerRef.current
    const { currentTime, duration } = player?.getTime?.() || {}
    const buffer = player?.getBufferInfo?.() || {}
    const { seeking, loading } = stateRef.current
    const lastProgressAt = lastProgressAtRef.current

    return withoutEmpty({
      diagnostics: player?.getDiagnostics?.(),
      phase: phaseRef.current,
      attempt: attemptRef.current,
      position: roundTime(currentTime),
      duration: roundTime(duration),
      isLive: player?.isLive?.(),
      bufferedAhead: roundTime(buffer.bufferedAhead),
      readyState: buffer.readyState,
      networkState: buffer.networkState,
      hasProgress: hasProgressRef.current,
      hadPlayback: hadPlaybackRef.current,
      sinceProgressMs: typeof lastProgressAt === 'number' ? Date.now() - lastProgressAt : undefined,
      activeStallMs: stallElapsedRef.current,
      activeLoadMs: loadElapsedRef.current,
      cycleBudgetMs: budgetRef.current,
      progressMs: progressRef.current,
      loading,
      seeking,
      userPaused: player?.isUserPaused?.(),
      visible: isVisibleRef.current,
    })
  }, [playerRef])

  // The cycle failed: the modal at once, one report once the stop of the last attempt is known.
  // stop: that stop while it runs; none when it already ended. A stop that fails is part of
  // the report; one that never settles is reported as pending after CANCEL_TIMEOUT
  const finishCycle = useCallback(
    // eslint-disable-next-line complexity
    (stop?: ?Promise<mixed>) => {
      if (!isActivePhase(phaseRef.current)) return

      const attempts = attemptsRef.current.map((entry) => ({ ...entry }))
      const errorText = toErrorText(messageRef.current)
      const failure = {
        cause: 'unknown',
        // The report keeps a stable text, whatever the language of the interface
        message: formatTechnicalMessage(messageRef.current),
        origin: originRef.current || undefined,
        cycleErrorCodes: cycleErrorCodesRef.current.slice(),
        attempts,
        attempt: loadsRef.current,
        cycle: cycleRef.current,
        history: historyRef.current.slice(),
        snapshot: snapshotRef.current,
      }

      // The handler of this content: a report that waits for the stop must not land on
      // the channel switched to meanwhile (it adds its own content id and engine)
      const onReport = onFailureRef.current

      switchRef.current = null
      cancelRef.current = null
      generationRef.current += 1
      setPhase(FAILED)
      onError({ message: errorText?.message || null, code: errorText?.code ?? null })

      let isReported = false
      let timer

      // eslint-disable-next-line complexity
      const report = (stopFailed: boolean, stopPending: boolean = false, stopError?: Object) => {
        if (isReported) return

        isReported = true
        clearTimeout(timer)

        const last: Object = attempts[attempts.length - 1]

        if (last && stopFailed) {
          last.stopFailed = true
          last.stopError = stopError
        }

        onReport?.({
          ...failure,
          // No attempt to blame: the stop before the first load of a Retry failed
          cause: last ? last.cause : 'cancel',
          error: last?.error,
          stop: stopPending ? 'pending' : (stopFailed && 'failed') || 'completed',
          stopError,
        })
      }

      if (!stop) {
        report(false)

        return
      }

      timer = setTimeout(() => report(false, true), CANCEL_TIMEOUT + LOADER_DELAY)
      stop
        .then(
          () => report(false),
          (error) =>
            report(true, false, {
              ...describePlaybackError(error),
              cause: error?.cause ? describePlaybackError(error.cause) : undefined,
            }),
        )
        .catch(() => {})
    },
    [setPhase, onError],
  )

  const hasBudgetForAttempt = () => CYCLE_BUDGET - budgetRef.current >= MIN_ATTEMPT_BUDGET

  // Loads the content again from the kept position, unless the cycle has no budget left
  const launchAttempt = useCallback(
    (isNext: boolean) => {
      if (!hasBudgetForAttempt()) {
        finishCycle()

        return
      }

      switchRef.current = null

      if (isNext) attemptRef.current += 1

      loadsRef.current += 1
      resetAttempt()
      setPhase(LOADING)
      playerRef.current?.reload?.({ startTime: resumeTimeRef.current })
    },
    [playerRef, finishCycle, resetAttempt, setPhase],
  )

  // Waits for the engine to stop. A failed or hung stop ends the cycle: nothing else is loaded
  const waitForStop = useCallback(
    (cancelled: Promise<mixed>, onStopped: () => void) => {
      const generation = generationRef.current

      switchRef.current = STOPPING

      return cancelled.then(
        () => {
          if (generation === generationRef.current) onStopped()

          return undefined
        },
        () => {
          if (generation !== generationRef.current) return undefined

          // The failed stop belongs to the attempt it was ending: not a load of its own
          return finishCycle(cancelled)
        },
      )
    },
    [finishCycle],
  )

  // An attempt failed: keeps its result, the position and the state, stops the engine,
  // then either starts the next attempt after a pause or ends the cycle
  const failAttempt = useCallback(
    // eslint-disable-next-line complexity
    (cause: string, message?: ?ErrorText, details?: PlaybackErrorDetails) => {
      if (!isActivePhase(phaseRef.current) || switchRef.current) return

      const player = playerRef.current

      // A timeout or an error with nothing to tell keeps the known cause
      if (message) messageRef.current = message

      snapshotRef.current = takeSnapshot()
      attemptsRef.current = [
        ...attemptsRef.current,
        withoutEmpty({
          attempt: attemptRef.current,
          cause,
          error: details,
          codes: attemptCodesRef.current.slice(),
          durationMs: Date.now() - attemptStartRef.current,
          snapshot: snapshotRef.current,
        }),
      ]
      // Snapshot above was taken before cancel resets the media element.

      if (player?.isLive?.()) {
        resumeTimeRef.current = null
      } else if (hasProgressRef.current) {
        resumeTimeRef.current = player?.getTime?.()?.currentTime
      }

      generationRef.current += 1

      const cancelled = new Promise((resolve) => {
        resolve(player?.cancel?.())
      })

      // Read by the retry; a failure here is handled where it is awaited
      cancelled.catch(() => {})
      cancelRef.current = cancelled

      const isLastAttempt =
        attemptRef.current >= MAX_ATTEMPTS || cause === 'cycle_timeout' || !hasBudgetForAttempt()

      if (isLastAttempt) {
        finishCycle(cancelled)

        return
      }

      // Recovering: a stalled video leaves the playing phase until an attempt plays again
      setPhase(LOADING)

      if (!stateRef.current.loading) onLoading()

      waitForStop(cancelled, () => {
        switchRef.current = PAUSING
        pauseLeftRef.current = RETRY_DELAY
      })
    },
    [playerRef, takeSnapshot, finishCycle, waitForStop, setPhase, onLoading],
  )

  // Keeps an error for the final report without reacting to it, such as a recoverable one
  // message: its text for the modal, should the cycle fail; it does not fail anything by itself
  // eslint-disable-next-line complexity
  const handleRecordError = useCallback((details: PlaybackErrorDetails, message?: ?ErrorText) => {
    if (!isActivePhase(phaseRef.current)) return

    historyRef.current = [...historyRef.current, { at: Date.now(), error: details }].slice(
      -HISTORY_LIMIT,
    )

    // Errors of the attempt being stopped are history only
    if (switchRef.current) return

    const code = details.code ?? details.details ?? 'unknown'

    // A new error needs its own stretch of stable playback before it is forgotten
    progressRef.current = 0

    // The newest meaningful text of the cycle; errors with nothing to tell keep the earlier one
    if (message) messageRef.current = message

    if (!attemptCodesRef.current.includes(code)) {
      attemptCodesRef.current = [...attemptCodesRef.current, code]
    }

    if (!cycleErrorCodesRef.current.includes(code)) {
      cycleErrorCodesRef.current = [...cycleErrorCodesRef.current, code]
    }

    if (!originRef.current) {
      originRef.current = details
    }
  }, [])

  // isTransient: an error the engine may still get over, such as a failed segment request
  const handlePlayerError = useCallback(
    // eslint-disable-next-line complexity
    (message: ?ErrorText, { isTransient = false, details = {} }: PlayerErrorOptions = {}) => {
      if (!isActivePhase(phaseRef.current)) return

      handleRecordError(details, message)

      // Between attempts a late error starts nothing
      if (switchRef.current) return

      const isMoving = hasProgressRef.current && stallElapsedRef.current < LOADER_DELAY

      // Kept above as the text of the cycle, used only if the playback does not recover
      if (isTransient && isMoving) return

      failAttempt('error', message, details)
    },
    [failAttempt, handleRecordError],
  )

  // A new cycle. The adapter is asked to stop again rather than trusting an earlier result:
  // a stop that is still running is returned as is, one that has ended since lets the load start
  const startNewCycle = useCallback(() => {
    generationRef.current += 1
    cycleRef.current += 1
    onResetError()
    startCycle(false)

    const cancelled = new Promise((resolve) => {
      resolve(playerRef.current?.cancel?.())
    })

    cancelled.catch(() => {})
    cancelRef.current = cancelled
    waitForStop(cancelled, () => launchAttempt(false))
  }, [playerRef, onResetError, startCycle, waitForStop, launchAttempt])

  const handleRetry = useCallback(() => {
    if (phaseRef.current === FAILED) startNewCycle()
  }, [startNewCycle])

  // After the modal was closed the engine holds no source: Play loads again the same way as Retry,
  // from the kept position. Any other Play (after a user pause, while loading) is the engine's own
  const handlePlayRequest = useCallback(() => {
    if (phaseRef.current !== DISMISSED) return false

    startNewCycle()

    return true
  }, [startNewCycle])

  // The engine was stopped by the failure: no notice, no loader, no more attempts
  const handleDismiss = useCallback(() => {
    if (phaseRef.current !== FAILED) return

    generationRef.current += 1
    switchRef.current = null
    setPhase(DISMISSED)
    onResetError()
    onStopLoading()
  }, [setPhase, onResetError, onStopLoading])

  const handleProgress = useCallback(
    // eslint-disable-next-line complexity
    (delta: number) => {
      // Recovery is confirmed by uninterrupted playback: a visible stall starts the count again
      if (stallElapsedRef.current >= LOADER_DELAY) {
        progressRef.current = 0
      }

      stallElapsedRef.current = 0
      hasProgressRef.current = true
      hadPlaybackRef.current = true
      progressRef.current += delta
      lastProgressAtRef.current = Date.now()

      if (stateRef.current.loading && !stateRef.current.seeking) {
        onStopLoading()
      }

      // Stable recovery, in either phase: the budget, the attempts and the causes start again;
      // a later stall is a new cycle. The count starts after the last stall or recorded error
      if (progressRef.current >= SUCCESS_PROGRESS) {
        attemptCodesRef.current = []
        resetCycle()

        if (phaseRef.current === LOADING) {
          loadElapsedRef.current = 0
          setPhase(PLAYING)
        }
      }
    },
    [onStopLoading, resetCycle, setPhase],
  )

  // Between attempts: the stop and the pause spend the budget, only while the playback is active.
  // A pause or a seek by the user holds them; the engine's own pause inside cancel() does not count
  const handleSwitchTick = useCallback(
    // eslint-disable-next-line complexity
    (delta: number) => {
      const isActive =
        isVisibleRef.current &&
        !isUserSeeking(playerRef.current, stateRef.current.seeking) &&
        !playerRef.current?.isUserPaused?.()

      if (!isActive) return

      budgetRef.current += delta

      if (budgetRef.current >= CYCLE_BUDGET) {
        finishCycle(switchRef.current === STOPPING ? cancelRef.current : null)

        return
      }

      if (switchRef.current === PAUSING) {
        pauseLeftRef.current -= delta

        if (pauseLeftRef.current <= 0) launchAttempt(true)
      }
    },
    [playerRef, finishCycle, launchAttempt],
  )

  // eslint-disable-next-line complexity
  const handleTick = useCallback(() => {
    const now = Date.now()
    const delta = Math.max(0, Math.min(now - lastTickRef.current, MAX_TICK))

    lastTickRef.current = now

    if (!isActivePhase(phaseRef.current)) return

    if (switchRef.current) {
      handleSwitchTick(delta)

      return
    }

    const player = playerRef.current
    const { currentTime, duration } = player?.getTime?.() || {}
    const lastTime = lastTimeRef.current
    const { seeking, loading } = stateRef.current

    if (typeof currentTime === 'number') {
      lastTimeRef.current = currentTime
    }

    const isActive =
      isVisibleRef.current &&
      !isUserSeeking(player, seeking) &&
      !player?.isUserPaused?.() &&
      !isEnded(currentTime, duration)

    // Only progress seen while the playback is active counts
    if (!isActive) return

    const buffer = player?.getBufferInfo?.()
    // A moving clock during an automatic seek is not evidence of playable video.
    const hasPlayableData =
      (!Number.isFinite(buffer?.readyState) || buffer.readyState >= 3) &&
      (!Number.isFinite(buffer?.bufferedAhead) || buffer.bufferedAhead > 0)

    if (
      !seeking &&
      hasPlayableData &&
      typeof currentTime === 'number' &&
      lastTime !== null &&
      currentTime - lastTime > MIN_PROGRESS
    ) {
      handleProgress(delta)

      return
    }

    // Waiting: for the first frame of the attempt or for a stalled video to move again
    stallElapsedRef.current += delta
    budgetRef.current += delta

    if (!hasProgressRef.current) {
      loadElapsedRef.current += delta
    }

    if (stallElapsedRef.current >= LOADER_DELAY && !loading) {
      onLoading()
    }

    if (budgetRef.current >= CYCLE_BUDGET) {
      failAttempt('cycle_timeout')
    } else if (!hasProgressRef.current && loadElapsedRef.current >= LOAD_TIMEOUT) {
      failAttempt('load_timeout')
    } else if (hasProgressRef.current && stallElapsedRef.current >= STALL_TIMEOUT) {
      failAttempt('stall_timeout')
    }
  }, [playerRef, handleSwitchTick, handleProgress, onLoading, failAttempt])

  const handleTickRef = useRef(handleTick)
  handleTickRef.current = handleTick

  useEffect(() => {
    lastTickRef.current = Date.now()

    const timer = setInterval(() => handleTickRef.current(), TICK)

    return () => {
      clearInterval(timer)
      generationRef.current += 1
    }
  }, [])

  // New content (for example a channel switch) starts a new cycle; the adapter loads it itself.
  // Layout effect: it runs before the adapter's own effect starts that load, so an error
  // of the new load (such as load_blocked) is never wiped by this reset
  useLayoutEffect(() => {
    if (contentVersion.isNew && contentVersion.prevValue) {
      generationRef.current += 1
      resumeTimeRef.current = defaultTimePosition
      cancelRef.current = null
      cycleRef.current = 0
      // History belongs to the content: errors of another channel say nothing about this one
      historyRef.current = []
      hadPlaybackRef.current = false
      lastProgressAtRef.current = null
      snapshotRef.current = {}

      onResetError()
      startCycle(true)
    }
  }, [contentVersion.value]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    onPlayerError: handlePlayerError,
    onRecordError: handleRecordError,
    onRetry: handleRetry,
    onDismiss: handleDismiss,
    onPlayRequest: handlePlayRequest,
  }
}

export default usePlaybackRecovery
